import { useState, type ReactNode } from 'react'
import { Check, Copy, Loader2 } from 'lucide-react'
import { Button } from '../../components/Button'
import { Modal } from '../../components/Modal'
import { useToast } from '../../context/ToastContext'
import { api, apiErrorMessage } from '../../lib/api'
import { ROLES, type Role } from '../../data/types'
import { Select } from '../../components/Select'

const roleLabels: Record<Role, string> = {
  Admin: 'Admin',
  Manager: 'Manager',
  'Team Member': 'Team Member',
  Viewer: 'Viewer',
}

const roleHint: Record<Role, string> = {
  Admin: 'Can see money, change the roster and permissions, and do everything else.',
  Manager: 'Runs the clients: sees every one, adds and archives them, staffs them. No money.',
  'Team Member': 'Does the work on the clients they are on. Not what any of them pay.',
  Viewer: 'Reads. Changes nothing.',
}

const WIRE_ROLE: Record<Role, string> = {
  Admin: 'partner',
  Manager: 'manager',
  'Team Member': 'team',
  Viewer: 'viewer',
}

/**
 * Putting somebody on the roster, and giving them a way in.
 *
 * Two acts, deliberately in one dialog: a person who exists but cannot sign in
 * is a row nobody notices is useless until they ask why the app will not let
 * them in. So this creates the person and immediately mints their setup link.
 *
 * The address is optional and it is what they sign in with. Leaving it blank
 * gives somebody a working account they reach by their short name, which is
 * how the roster worked before addresses existed and is still fine.
 *
 * It is set in a second call, because `people.create` deliberately does not
 * take one: the address is the way in, and the operation that changes a way
 * in is its own audited act rather than a field on a larger form.
 *
 * The link is still shown here to be sent by hand. Nothing in this platform
 * sends email, so an address on file is a way to sign in, never a place a
 * password reset arrives.
 *
 * The link is returned once and cannot be read back, because the server cannot
 * read it back either: it stores a hash. Closing this dialog without copying it
 * means minting a new one, which is the correct trade for a URL that lets
 * whoever holds it set somebody's password.
 */
export function AddTeamMemberModal({
  open,
  onClose,
  onInvited,
}: {
  open: boolean
  onClose: () => void
  onInvited?: () => void
}) {
  const { showToast } = useToast()
  const [form, setForm] = useState({ name: '', role: 'Team Member' as Role, capacity: '', email: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  function reset() {
    setForm({ name: '', role: 'Team Member', capacity: '' , email: '' })
    setError(null)
    setInviteLink(null)
    setCopied(false)
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function handleInvite() {
    setError(null)
    setSubmitting(true)
    try {
      const person = await api.post<{ id: string }>('/people', {
        name: form.name.trim(),
        role: WIRE_ROLE[form.role],
        ...(form.capacity.trim() === '' ? {} : { capacity: Number(form.capacity) }),
      })
      // Before the link, so a failure here — a duplicate address is the
      // likely one — is seen while the dialog is still asking, rather than
      // after it has handed over a link and looks finished.
      const email = form.email.trim().toLowerCase()
      if (email !== '') {
        await api.post(`/people/${encodeURIComponent(person.id)}/email`, { email })
      }

      const invite = await api.post<{ url: string }>(
        `/people/${encodeURIComponent(person.id)}/invite`,
      )
      // Absolute, because it is going to be pasted into WhatsApp.
      setInviteLink(new URL(invite.url, window.location.origin).toString())
      onInvited?.()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function copyLink() {
    if (!inviteLink) return
    await navigator.clipboard.writeText(inviteLink)
    setCopied(true)
    showToast('Setup link copied to clipboard.')
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={inviteLink ? 'Send them this' : 'Add somebody'}
      description={
        inviteLink
          ? 'Nothing here sends email, so send this to them yourself.'
          : 'They go on the roster straight away. Their name becomes an alias, so the agent recognises them in a message from the first day.'
      }
      footer={
        inviteLink ? (
          <Button onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={submitting || !form.name.trim()}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : 'Add and make a link'}
            </Button>
          </>
        )
      }
    >
      {inviteLink ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-shell-border bg-shell-border/30 px-3 py-2.5">
            <p className="min-w-0 flex-1 truncate text-sm text-shell-text-active">{inviteLink}</p>
            <button
              onClick={copyLink}
              aria-label="Copy setup link"
              className="shrink-0 rounded-lg p-1.5 text-shell-text hover:bg-shell-border hover:text-shell-text-active"
>
              {copied ? <Check size={15} className="text-success-500" /> : <Copy size={15} />}
            </button>
          </div>
          <p className="text-xs text-shell-text">
            It lasts a week and works once. It is shown here and nowhere else — the server keeps
            only a hash of it — so copy it now. You can make a fresh one from the roster any time,
            which stops this one working.
          </p>
</div>
      ) : (
        <div className="flex flex-col gap-4">
          {error && (
            <p role="alert" className="rounded-xl bg-danger-50 px-3.5 py-2.5 text-sm text-danger-600">
              {error}
            </p>
          )}
          <Field label="Name">
            <input
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Aparupaa"
              className="field-input"
/>
          </Field>
          <Field label="Email address">
            <input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="optional — they can sign in by name without it"
              className="field-input"
/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="What they can do">
              <Select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
                className="field-input"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabels[r]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Effort points a week">
              <input
                inputMode="numeric"
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                placeholder="optional"
                className="field-input"
              />
            </Field>
          </div>
          <p className="text-xs text-shell-text">{roleHint[form.role]}</p>
          <p className="text-xs text-shell-text">
            Capacity is what the roster measures a workload against. Leave it blank and their bar
            reads zero rather than guessing at a number nobody agreed.
          </p>
</div>
      )}
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="field-label">
        {label}
      </label>
{children}
    </div>
  )
}
